import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Pagination } from '@/components/shared/Pagination';

export const ExpenseTableView = ({ expenses, getCategoryById, currencySymbol, canWrite, canDelete, onEdit, onDelete, onTogglePaye, currentPage, totalPages, startIndex, itemsPerPage, totalItems, onPageChange }) => (
  <Card className="border-0 shadow-md">
    <CardHeader><CardTitle className="text-xl">Liste des dépenses</CardTitle></CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200 dark:border-gray-800">
              <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Date</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Catégorie</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 hidden md:table-cell">Description</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700 hidden lg:table-cell">Quantité</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Montant</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Statut</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-8 text-gray-500 dark:text-gray-400">Aucune dépense enregistrée</td></tr>
            ) : (
              expenses.map((expense) => {
                const category = getCategoryById(expense.categorie_id);
                return (
                  <tr key={expense.id} className="border-b border-gray-100 hover:bg-gray-50 dark:hover:bg-gray-900">
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{new Date(expense.date).toLocaleDateString('fr-FR')}</td>
                    <td className="py-3 px-4">
                      {category ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.couleur }}></div>
                          <span className="text-gray-900 dark:text-white">{category.nom}</span>
                        </div>
                      ) : <span className="text-gray-500 dark:text-gray-400">Supprimée</span>}
                    </td>
                    <td className="py-3 px-4 text-gray-600 hidden md:table-cell max-w-48 truncate">{expense.description || '-'}</td>
                    <td className="py-3 px-4 text-center text-gray-600 hidden lg:table-cell">{expense.quantite ? `${expense.quantite} ${expense.unite || ''}` : '-'}</td>
                    <td className="py-3 px-4 text-right font-semibold text-gray-900 dark:text-white">{expense.montant.toFixed(2)} {currencySymbol}</td>
                    <td className="py-3 px-4 text-center">
                      <PaymentBadge expense={expense} canWrite={canWrite} onToggle={onTogglePaye} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex gap-1 justify-end">
                        {canWrite && <Button variant="ghost" size="sm" onClick={() => onEdit(expense)} className="text-blue-600 hover:bg-blue-50" title="Modifier"><Edit className="w-4 h-4" /></Button>}
                        {canDelete && <Button variant="ghost" size="sm" onClick={() => onDelete(expense.id)} className="text-red-600 hover:bg-red-50" title="Supprimer"><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} startIndex={startIndex} itemsPerPage={itemsPerPage} totalItems={totalItems} label="dépenses" />
    </CardContent>
  </Card>
);

export const ExpenseCardView = ({ expenses, getCategoryById, currencySymbol, canWrite, canDelete, onEdit, onDelete, onTogglePaye, currentPage, totalPages, onPageChange }) => (
  <Card className="border-0 shadow-md">
    <CardHeader><CardTitle className="text-xl">Dépenses en cartes</CardTitle></CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {expenses.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400"><p>Aucune dépense enregistrée</p></div>
        ) : (
          expenses.map((expense) => {
            const category = getCategoryById(expense.categorie_id);
            return (
              <Card key={expense.id} className="border hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 dark:text-white">{new Date(expense.date).toLocaleDateString('fr-FR')}</div>
                        {category && (
                          <div className="flex items-center gap-2 mt-1">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.couleur }}></div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">{category.nom}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-slate-700 dark:text-slate-300">{expense.montant.toFixed(2)} {currencySymbol}</div>
                        {expense.quantite && <div className="text-xs text-gray-500 dark:text-gray-400">{expense.quantite} {expense.unite}</div>}
                        <PaymentBadge expense={expense} canWrite={canWrite} onToggle={onTogglePaye} compact />
                      </div>
                    </div>
                    {expense.description && <div className="text-sm text-gray-600 bg-gray-50 dark:bg-gray-950 p-2 rounded">{expense.description}</div>}
                    {(canWrite || canDelete) && (
                      <div className="flex gap-2 pt-2 border-t border-gray-100">
                        {canWrite && <Button variant="outline" size="sm" onClick={() => onEdit(expense)} className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"><Edit className="w-4 h-4 mr-2" />Modifier</Button>}
                        {canDelete && <Button variant="outline" size="sm" onClick={() => onDelete(expense.id)} className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} startIndex={0} itemsPerPage={expenses.length} totalItems={expenses.length} label="dépenses" />
    </CardContent>
  </Card>
);

const PaymentBadge = ({ expense, canWrite, onToggle, compact = false }) => (
  <button
    onClick={() => onToggle(expense)}
    disabled={!canWrite}
    className={`${compact ? 'mt-2 ' : ''}inline-flex items-center gap-1 px-${compact ? '2' : '3'} py-${compact ? '0.5' : '1'} rounded-full text-xs font-medium transition-colors ${
      expense.paye ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
    } ${!canWrite ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    title={canWrite ? 'Cliquez pour changer le statut' : 'Permission insuffisante'}
    data-testid={`toggle-paye-${expense.id}`}
  >
    {expense.paye ? <><CheckCircle className="w-3 h-3" />Payé</> : <><XCircle className="w-3 h-3" />Non payé</>}
  </button>
);
